import React, { useEffect, useMemo, useState } from 'react';
import { isBlank } from 'underscore.string';
import NoSsr from '@material-ui/core/NoSsr';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { removeTypename } from 'shared/helpers/utils';
import { useKioskPro } from 'src/hooks/use-kiosk-pro';

import { MainContent } from 'components/skip-to-main';
import { useModals, ModalNames } from 'components/modals';
import useUser from 'hooks/use-user';
import { getPersistedValue } from 'shared/utils/persisted-values';

import useUI from 'hooks/use-ui';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { getHtmlPlainText } from 'shared/utils/misc-utils';
import useDispensaryCategories from 'src/dispensary/hooks/use-dispensary-categories';
import { AmplitudeEvents, useAmplitude } from 'shared/hooks/use-amplitude';
import useCart from 'shared/hooks/use-cart';
import { useRemoteComponent, RemoteComponentSlot } from 'hooks/use-remote-component';

import { MenuImageBanner } from 'src/components/menu-image-banner';
import { PersonalizedProducts } from 'components/carousels/products-carousels/personalized-products-carousel';
import { HomePageSponsoredBrandCarousel } from 'src/components/carousels/ads-carousels/home-page-sponsored-brand-carousel';
import { CategoriesNav } from 'src/components/categories-nav';
import { useAdsEnabled } from 'src/utils/ads/hooks/use-ads-enabled';
import { LoyaltyV1 } from 'src/components/widgets/loyalty-tiers/loyaltyV1';
import { LoyaltyV2 } from 'src/components/widgets/loyalty-tiers/loyaltyV2';
import { CashRedemption } from 'src/components/widgets/rewards/cash-redemption';
import { DiscountRedemption } from 'src/components/widgets/rewards/discount-redemption';
import { useLoyaltyV2 } from 'src/components/widgets/loyalty-tiers/use-loyaltyV2';
import { RedemptionType } from 'src/components/loyalty/rewards/dpos-utils';
import { LoginCta } from 'src/components/ctas';
import SmartBanner from 'src/components/meta-tags/smart-banner';

import { useProductsForHomePage } from 'src/dispensary/hooks/use-products-for-home-page';
import { HomepageCarouselSections } from 'components/homepage-carousel-sections';

import MenuBanner from './components/menu-banner';

export default function CoreMenuDispensaryPage(): JSX.Element | null {
  const UI = useUI();
  const { dispensary } = useDispensary();
  const { categories } = useDispensaryCategories();
  const amplitude = useAmplitude();
  const Cart = useCart();
  const flags = useFlags();
  const dispensaryId = dispensary?.id;
  const { openModal } = useModals();
  const user = useUser();
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const { redemptionType, hasTiers } = useLoyaltyV2();

  // Show order success modals when viewOrderId is stored in session storage for embedded menus
  const isForceFullWidthCheckoutEnabled = flags['fintech.platform.force-full-width-checkout'] ?? false;
  const isLoyaltyPersonalizationCtaEnabled =
    flags['growth.ecomm.logged-out-loyalty-personalization-cta.rollout'] ?? false;
  const isNewLoyaltyWidgetEnabled = flags['pos.ecomm.show-new-loyalty-widget.rollout'] ?? false;
  const isKioskProEnabled = useKioskPro();
  const adsEnabled = useAdsEnabled();

  const RemoteHero = useRemoteComponent(RemoteComponentSlot.Hero);

  // don't show anonymous checkout for medical menus/orders ENG-52870
  const useAnonymousKioskCheckout =
    dispensary?.orderTypesConfigV2.kiosk?.anonymousCheckout && Cart.menuType === `rec` && !isKioskProEnabled;

  const productsForHomePage = useProductsForHomePage({ dispensaryId });

  const sponsoredBrandCarouselEnabled =
    flags['growth.ads.home-page-sponsored-brand-carousel.config']?.[UI.variant] ?? false;
  const showHomePageSponsoredBrandCarousel = adsEnabled && sponsoredBrandCarouselEnabled;

  useEffect(() => {
    amplitude.log(AmplitudeEvents.menu.viewHomepage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const DOMParser = typeof window !== 'undefined' ? window.DOMParser : null;
  const bannerIsBlank = useMemo(
    () => !dispensary?.menuBannerHtml || isBlank(getHtmlPlainText(dispensary?.menuBannerHtml, DOMParser)),
    [dispensary?.menuBannerHtml, DOMParser]
  );

  const showMenuBanner =
    !bannerIsBlank && UI.isVariant([`store-front`, `embedded`, ...(useAnonymousKioskCheckout ? [`kiosk`] : [])]);

  // Effect to handle page load event
  useEffect(() => {
    function handleLoad(): void {
      setIsPageLoaded(true);
      window.removeEventListener('load', handleLoad);
    }

    setIsPageLoaded(document.readyState === 'complete');

    if (document.readyState !== 'complete') {
      window.addEventListener('load', handleLoad);
    }
  }, []);

  // Effect to handle modal opening after page load
  useEffect(() => {
    if (
      !isForceFullWidthCheckoutEnabled ||
      !UI.isEmbedded ||
      !dispensary?.embedSettings?.enableFullScreenEmbeddedCheckout ||
      !isPageLoaded
    ) {
      return;
    }

    const storedOrderId = getPersistedValue('viewOrderId');
    const stashedOrder = getPersistedValue('stashedOrder');

    if (storedOrderId) {
      if (!user.hasExternalUser && stashedOrder) {
        // For guest users, show guest checkout success modal with order data
        try {
          Cart.stashedOrder = JSON.parse(stashedOrder);
          UI.viewingGuestOrderSuccess = true;
        } catch (error) {
          // If JSON parsing fails, log error and continue without showing success modal
          console.error('Failed to parse stashed order:', error);
          UI.viewingGuestOrderSuccess = false;
        }
      } else {
        // For logged in users, show order status modal
        user.viewOrderId = storedOrderId;
        void openModal(ModalNames.orderStatus);
      }
    }
  }, [
    user,
    openModal,
    Cart,
    UI,
    dispensary?.embedSettings?.enableFullScreenEmbeddedCheckout,
    isForceFullWidthCheckoutEnabled,
    isPageLoaded,
  ]);

  if (!dispensary) {
    return null;
  }

  return (
    <MainContent>
      <SmartBanner />

      <RemoteHero />

      {!!dispensary?.imageBanners?.length && !UI.isMobileEcommApp && (
        <MenuImageBanner banners={removeTypename(dispensary.imageBanners)} />
      )}

      <NoSsr>
        {showMenuBanner && <MenuBanner htmlContent={dispensary.menuBannerHtml} color={dispensary.menuBannerColor} />}
      </NoSsr>

      <div data-testid='homepage-categories'>
        <CategoriesNav categories={categories} />
      </div>

      {isNewLoyaltyWidgetEnabled ? (
        <>
          {hasTiers && <LoyaltyV2 />}
          {!hasTiers && redemptionType === RedemptionType.discount && <DiscountRedemption displayCTA />}
          {!hasTiers && redemptionType === RedemptionType.cash && <CashRedemption displayCTA />}
        </>
      ) : (
        <LoyaltyV1 />
      )}

      {isLoyaltyPersonalizationCtaEnabled && <LoginCta margin='20px auto 40px' hideIfNeitherEnabled />}

      {showHomePageSponsoredBrandCarousel && <HomePageSponsoredBrandCarousel />}

      {(!UI.isKiosk || isKioskProEnabled) && <PersonalizedProducts {...productsForHomePage} />}

      <HomepageCarouselSections />
    </MainContent>
  );
}
